/**
 * Analysis Engine
 * Calculates derived financial metrics and evaluation criteria
 * from raw financial statement data.
 */

import type {
  AnnualIncomeStatement,
  AnnualBalanceSheet,
  AnnualCashFlow,
  StockOverview,
  AnnualMetrics,
  EvaluationCriteria,
  EvaluationStatus,
  SectorAnalysis,
  EvaluationReport,
  GicsSector,
  SectorClassification,
} from '@/types/stock';
import { SECTOR_CLASSIFICATIONS } from '@/types/stock';
import type { FinancialData } from './financialDataService';

// ============================================================
// Helpers
// ============================================================

function pct(value: number, total: number): number | null {
  if (!total || total === 0) return null;
  return (value / total) * 100;
}

function yoyGrowth(current: number, prior: number): number | null {
  if (!prior || prior === 0) return null;
  return ((current - prior) / Math.abs(prior)) * 100;
}

// ============================================================
// Metric Calculation
// ============================================================

export function calculateMetrics(
  income: AnnualIncomeStatement[],
  balance: AnnualBalanceSheet[],
  cashFlow: AnnualCashFlow[],
  startYear: number,
  endYear: number,
): AnnualMetrics[] {
  // Filter to requested period (with one extra year before for growth calcs)
  const allYears = income.map((i) => i.fiscalYear);
  const minYear = Math.min(...allYears, startYear) - 1; // include one year prior for growth

  const filteredIncome = income.filter((i) => i.fiscalYear >= minYear && i.fiscalYear <= endYear);
  const filteredBalance = balance.filter((b) => b.fiscalYear >= minYear && b.fiscalYear <= endYear);
  const filteredCashFlow = cashFlow.filter((c) => c.fiscalYear >= minYear && c.fiscalYear <= endYear);

  const balanceMap = new Map(filteredBalance.map((b) => [b.fiscalYear, b]));
  const cashFlowMap = new Map(filteredCashFlow.map((c) => [c.fiscalYear, c]));

  const metrics: AnnualMetrics[] = [];

  for (let i = 0; i < filteredIncome.length; i++) {
    const inc = filteredIncome[i];
    const prior = filteredIncome[i - 1];
    const bs = balanceMap.get(inc.fiscalYear);
    const cf = cashFlowMap.get(inc.fiscalYear);

    // FCF: try from cash flow, fallback to operating - capex
    let fcf = cf?.freeCashFlow ?? 0;
    if (fcf === 0 && cf) {
      fcf = cf.operatingCashFlow - Math.abs(cf.capitalExpenditures);
    }

    const row: AnnualMetrics = {
      fiscalYear: inc.fiscalYear,
      revenue: inc.revenue,
      revenueGrowth: prior ? yoyGrowth(inc.revenue, prior.revenue) : null,
      grossProfit: inc.grossProfit,
      grossProfitGrowth: prior ? yoyGrowth(inc.grossProfit, prior.grossProfit) : null,
      grossMargin: pct(inc.grossProfit, inc.revenue),
      netIncome: inc.netIncome,
      profitMargin: pct(inc.netIncome, inc.revenue),
      operatingMargin: pct(inc.operatingIncome, inc.revenue),
      eps: inc.eps,
      epsGrowth: prior ? yoyGrowth(inc.eps, prior.eps) : null,
      currentRatio: bs && bs.currentLiabilities > 0
        ? bs.currentAssets / bs.currentLiabilities
        : null,
      debtToEquity: bs && bs.totalEquity > 0
        ? bs.longTermDebt / bs.totalEquity
        : null,
      debtToAssets: bs && bs.totalAssets > 0
        ? bs.totalLiabilities / bs.totalAssets
        : null,
      freeCashFlow: fcf,
      fcfMargin: pct(fcf, inc.revenue),
      fcfGrowth: null, // filled in next pass
    };

    metrics.push(row);
  }

  // Fill in FCF growth
  for (let i = 1; i < metrics.length; i++) {
    const cur = metrics[i];
    const prev = metrics[i - 1];
    if (prev.freeCashFlow !== 0) {
      cur.fcfGrowth = yoyGrowth(cur.freeCashFlow, prev.freeCashFlow);
    }
  }

  // Return only the requested period
  return metrics.filter((m) => m.fiscalYear >= startYear && m.fiscalYear <= endYear);
}

// ============================================================
// Criteria Evaluation
// ============================================================

export interface EvaluationSettings {
  profitMarginTarget: number;  // e.g. 15
  currentRatioTarget: number;  // e.g. 1.0
}

function avgOrNull(values: Array<number | null>): number | null {
  const nums = values.filter((v): v is number => v !== null);
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

export function evaluateCriteria(
  metrics: AnnualMetrics[],
  settings: EvaluationSettings,
): EvaluationCriteria[] {
  const criteria: EvaluationCriteria[] = [];

  // 1. Profit Margin > target%
  const avgProfitMargin = avgOrNull(metrics.map((m) => m.profitMargin));
  criteria.push({
    label: 'Avg Net Profit Margin',
    value: avgProfitMargin,
    target: `≥ ${settings.profitMarginTarget}%`,
    passed: avgProfitMargin !== null ? avgProfitMargin >= settings.profitMarginTarget : null,
    description: `Average net income as percentage of revenue over the analysis period. Target: >${settings.profitMarginTarget}%.`,
  });

  // 2. Revenue growth consistency (at least 60% of years positive)
  const revenueGrowths = metrics.map((m) => m.revenueGrowth).filter((v) => v !== null) as number[];
  const positiveRevGrowthPct = revenueGrowths.length > 0
    ? (revenueGrowths.filter((v) => v > 0).length / revenueGrowths.length) * 100
    : null;
  criteria.push({
    label: 'Revenue Growth Consistency',
    value: positiveRevGrowthPct,
    target: '≥ 60% of years positive',
    passed: positiveRevGrowthPct !== null ? positiveRevGrowthPct >= 60 : null,
    description: 'Percentage of analysis years where revenue grew year-over-year.',
  });

  // 3. Gross profit growth consistency
  const gpGrowths = metrics.map((m) => m.grossProfitGrowth).filter((v) => v !== null) as number[];
  const positiveGpGrowthPct = gpGrowths.length > 0
    ? (gpGrowths.filter((v) => v > 0).length / gpGrowths.length) * 100
    : null;
  criteria.push({
    label: 'Gross Profit Growth Consistency',
    value: positiveGpGrowthPct,
    target: '≥ 60% of years positive',
    passed: positiveGpGrowthPct !== null ? positiveGpGrowthPct >= 60 : null,
    description: 'Percentage of analysis years where gross profit grew year-over-year.',
  });

  // 4. EPS growth consistency
  const epsGrowths = metrics.map((m) => m.epsGrowth).filter((v) => v !== null) as number[];
  const positiveEpsGrowthPct = epsGrowths.length > 0
    ? (epsGrowths.filter((v) => v > 0).length / epsGrowths.length) * 100
    : null;
  criteria.push({
    label: 'EPS Growth Consistency',
    value: positiveEpsGrowthPct,
    target: '≥ 60% of years positive',
    passed: positiveEpsGrowthPct !== null ? positiveEpsGrowthPct >= 60 : null,
    description: 'Percentage of analysis years where EPS grew year-over-year.',
  });

  // 5. Current Ratio > target (average)
  const avgCurrentRatio = avgOrNull(metrics.map((m) => m.currentRatio));
  criteria.push({
    label: 'Avg Current Ratio',
    value: avgCurrentRatio,
    target: `≥ ${settings.currentRatioTarget}x`,
    passed: avgCurrentRatio !== null ? avgCurrentRatio >= settings.currentRatioTarget : null,
    description: 'Average current assets divided by current liabilities. Higher = better liquidity.',
  });

  // 6. Free Cash Flow positive trend (at least 50% of years positive)
  const fcfValues = metrics.map((m) => m.freeCashFlow);
  const positiveFcfPct = fcfValues.length > 0
    ? (fcfValues.filter((v) => v > 0).length / fcfValues.length) * 100
    : null;
  criteria.push({
    label: 'Free Cash Flow Positivity',
    value: positiveFcfPct,
    target: '≥ 70% of years positive',
    passed: positiveFcfPct !== null ? positiveFcfPct >= 70 : null,
    description: 'Percentage of analysis years where free cash flow was positive.',
  });

  // 7. Gross Margin stability (std dev low or avg > 30%)
  const avgGrossMargin = avgOrNull(metrics.map((m) => m.grossMargin));
  criteria.push({
    label: 'Avg Gross Margin',
    value: avgGrossMargin,
    target: '≥ 30%',
    passed: avgGrossMargin !== null ? avgGrossMargin >= 30 : null,
    description: 'Average gross profit margin. Higher margins may indicate competitive advantages.',
  });

  return criteria;
}

// ============================================================
// Overall Score & Status
// ============================================================

export function calculateScore(criteria: EvaluationCriteria[]): number {
  const applicable = criteria.filter((c) => c.passed !== null);
  if (applicable.length === 0) return 0;
  const passed = applicable.filter((c) => c.passed === true).length;
  return Math.round((passed / applicable.length) * 100);
}

export function calculateStatus(score: number): EvaluationStatus {
  if (score >= 70) return 'PASS';
  if (score >= 40) return 'WATCH';
  if (score > 0) return 'FAIL';
  return 'PENDING';
}

// ============================================================
// Sector Analysis
// ============================================================

const GICS_CODES: Record<GicsSector, string> = {
  'Energy': '10',
  'Materials': '15',
  'Industrials': '20',
  'Consumer Discretionary': '25',
  'Consumer Staples': '30',
  'Health Care': '35',
  'Financials': '40',
  'Information Technology': '45',
  'Communication Services': '50',
  'Utilities': '55',
  'Real Estate': '60',
  'Unknown': '00',
};

export function buildSectorAnalysis(
  overview: StockOverview,
  metrics: AnnualMetrics[],
): SectorAnalysis {
  const sector = overview.sector;
  const classification: SectorClassification = SECTOR_CLASSIFICATIONS[sector] ?? 'mature';

  const avgProfitMargin = avgOrNull(metrics.map((m) => m.profitMargin)) ?? 0;
  const avgGrossMargin = avgOrNull(metrics.map((m) => m.grossMargin)) ?? 0;
  const avgFcfMargin = avgOrNull(metrics.map((m) => m.fcfMargin)) ?? 0;

  const moatIndicators: string[] = [];

  if (avgProfitMargin > 20) moatIndicators.push('High net profit margins (>20%)');
  else if (avgProfitMargin > 15) moatIndicators.push('Above-average profit margins (>15%)');

  if (avgGrossMargin > 50) moatIndicators.push('Strong gross margins (>50%) — pricing power');
  else if (avgGrossMargin > 35) moatIndicators.push('Healthy gross margins (>35%)');

  if (avgFcfMargin > 15) moatIndicators.push('High free cash flow generation (>15%)');

  if (overview.beta !== null && overview.beta < 0.8) {
    moatIndicators.push('Low beta — defensive characteristics');
  }

  if (moatIndicators.length === 0) {
    moatIndicators.push('No strong moat indicators detected from available data');
  }

  return {
    gicsSector: sector,
    gicsCode: GICS_CODES[sector] ?? '00',
    classification,
    industry: overview.industry,
    moatIndicators,
    competitiveNotes: '',
  };
}

// ============================================================
// Full Report Builder
// ============================================================

export function buildEvaluationReport(
  id: string,
  ticker: string,
  financialData: FinancialData,
  startYear: number,
  endYear: number,
  settings: EvaluationSettings,
  dataSource: EvaluationReport['dataSource'],
  existingComments?: string,
): EvaluationReport {
  const { income, balance, cashFlow, overview } = financialData;

  const annualMetrics = calculateMetrics(income, balance, cashFlow, startYear, endYear);
  const criteria = evaluateCriteria(annualMetrics, settings);
  const score = calculateScore(criteria);
  const overallStatus = calculateStatus(score);
  const sectorAnalysis = buildSectorAnalysis(overview, annualMetrics);

  return {
    id,
    ticker: ticker.toUpperCase(),
    companyName: overview.name,
    exchange: overview.exchange,
    evaluationDate: Math.floor(Date.now() / 1000),
    analysisPeriod: { startYear, endYear },
    annualMetrics,
    overview,
    sectorAnalysis,
    criteria,
    overallStatus,
    score,
    comments: existingComments ?? '',
    tags: [],
    dataSource,
  };
}

// ============================================================
// Utility: format large numbers
// ============================================================

export function formatCurrency(value: number, compact = true): string {
  if (!isFinite(value)) return 'N/A';
  if (compact) {
    if (Math.abs(value) >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
  }
  return `$${value.toLocaleString()}`;
}

export function formatNumber(value: number | null, decimals = 1): string {
  if (value === null || !isFinite(value)) return 'N/A';
  return value.toFixed(decimals);
}

export function formatPercent(value: number | null, decimals = 1): string {
  if (value === null || !isFinite(value)) return 'N/A';
  return `${value.toFixed(decimals)}%`;
}
